/* eslint-disable react/require-default-props, react/no-unused-prop-types */
import React from 'react'
import { TextField, InputAdornment } from '@material-ui/core'
import {
  Control,
  RegisterOptions,
  Controller,
  FieldError,
  InputState,
} from 'react-hook-form'
import { SizeOptions } from 'TypeDefs/FormSongs'
import useStyles from './InputSong.styles'

export type ReactHookFormRenderProp = {
  value: any
  inputState: InputState
}

type InputSongProps = {
  name: string
  label?: string
  type: string
  multiline?: boolean
  rowsMax?: number
  value?: string
  register?: (rules: RegisterOptions) => void
  control?: Control
  rules?: RegisterOptions
  required?: boolean
  error?: FieldError
  validationError?: boolean
  header?: string
  subheader?: string
  size?: SizeOptions
  maxLength?: number
  placeholder?: string
  titleWithTooltip?: string
  tooltipIndex?: number
  tooltipText?: string
  helperMessage?: string | React.ReactElement
  defaultValue?: string
  helperText?: string
  inputProps?: Record<string, unknown>
  /** Description of prop "onBlurHandler".
   *
   * If you are setting state or calling hooks that will cause re-render,
   * then re-consider because that defeats the purpose of react-hook-form
   */
  onBlurHandler?: (
    e: React.SyntheticEvent,
    reactHookFormProps: ReactHookFormRenderProp
  ) => void
  /** Description of prop "onChangeHandler".
   *
   * If you are setting state or calling hooks that will cause re-render,
   * then re-consider because that defeats the purpose of react-hook-form
   */
  onChangeHandler?: (
    e: React.SyntheticEvent,
    reactHookFormProps: ReactHookFormRenderProp
  ) => void
  onFocusHandler?: (
    e: React.SyntheticEvent,
    reactHookFormProps: ReactHookFormRenderProp
  ) => void
  onKeydownHandler?: (
    e: React.KeyboardEvent,
    reactHookFormProps: ReactHookFormRenderProp
  ) => void
  inputValidator?: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void
  disabled?: boolean
  rows?: number
  ref?: any
  showRequiredOnLabel?: boolean
  customRequiredErrorMessage?: string
  style?: Record<string, any>
  autoFocus?: boolean
  inputAdornment?: React.ReactElement
}

/**
 *
 * @param param0
 * @returns
 */

const InputSong = ({
  label,
  name,
  control,
  required,
  error,
  validationError,
  type,
  maxLength,
  rowsMax,
  placeholder,
  disabled,
  rows,
  inputProps,
  helperText,
  onBlurHandler,
  onChangeHandler,
  onKeydownHandler,
  inputValidator,
  onFocusHandler,
  // ref,
  defaultValue,
  showRequiredOnLabel,
  customRequiredErrorMessage,
  style,
  autoFocus,
  inputAdornment,
}: InputSongProps) => {
  const labels: { [key: string]: any } = {
    phone: {
      regex: false,
      message: 'Invalid Phone',
    },
    email: {
      regex: new RegExp(
        /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      ),
      message: 'Invalid Email',
    },
    defaultValidation: {
      regex: false,
      message: 'Invalid',
    },
    text: {
      regex: false,
      message: 'Invalid',
    },
    url: {
      regex: new RegExp(
        // eslint-disable-next-line no-useless-escape
        /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/
      ),
      message: 'Please enter a valid URL (for example https://www.resilia.com)',
    },
    ein: {
      regex: new RegExp(/^\d{9}$/),
      message: 'Please check your EIN (must be 9-digits, no dashes)',
    },
    year: {
      regex: new RegExp(/^[12][0-9]{3}$/),
      message: 'Please enter a valid year',
    },
    numBoardMembers: {
      regex: new RegExp(/^[0-9]*$/),
      message: 'Please enter a valid number',
    },
  }
  const getRequired =
    customRequiredErrorMessage || (required ? 'This field is required' : '')
  const getMaxChars = maxLength
    ? { value: maxLength, message: `Max ${maxLength} Chars` }
    : ''
  const getMultiLine = !!(rowsMax && rowsMax > 1)
  const getRegex = type in labels ? labels[type] : labels.defaultValidation

  const classes = useStyles({ style })

  return (
    <Controller
      render={({ onChange, onBlur, ref, value }, inputState) => (
        <TextField
          autoFocus={autoFocus}
          disabled={!!disabled}
          fullWidth
          name={name}
          variant='outlined'
          color='primary'
          label={
            showRequiredOnLabel ? (
              <>
                <span className={classes.red}>* </span>
                {label}
              </>
            ) : (
              label
            )
          }
          aria-label={label}
          rowsMax={rowsMax}
          rows={rows}
          multiline={getMultiLine}
          placeholder={placeholder}
          type={
            type === 'password' ||
            type === 'date' ||
            type === 'number' ||
            type === 'email'
              ? type
              : 'text'
          }
          helperText={helperText}
          inputProps={{ ...inputProps, 'data-testid': name }}
          InputProps={{
            ...(type === 'date' && {
              startAdornment: <InputAdornment position='start' />,
            }),
            ...(inputAdornment && {
              startAdornment: (
                <InputAdornment position='start'>
                  {inputAdornment}
                </InputAdornment>
              ),
            }),
          }}
          onChange={(e) => {
            if (inputValidator) {
              const validatedInput = inputValidator(e)
              onChange(validatedInput)
            } else {
              onChange(e)
            }
            if (onChangeHandler) {
              onChangeHandler(e, { value, inputState })
            }
          }}
          onFocus={(e) => {
            if (onFocusHandler) {
              onFocusHandler(e, { value, inputState })
            }
          }}
          onBlur={(e) => {
            onBlur()
            if (onBlurHandler) {
              onBlurHandler(e, { value, inputState })
            }
          }}
          onKeyDown={(e) => {
            if (onKeydownHandler) {
              onKeydownHandler(e, { value, inputState })
            }
          }}
          inputRef={ref}
          value={value}
          className={classes.input}
          error={!!error || validationError}
          style={style}
        />
      )}
      name={name}
      control={control}
      rules={{
        required: getRequired,
        pattern: {
          value: getRegex.regex,
          message: getRegex.message,
        },
        maxLength: getMaxChars,
      }}
      error={!!error}
      defaultValue={defaultValue}
    />
  )
}

export default InputSong
